import { atom } from "recoil"
import * as z from "zod"

import { jsonDbAtom, secondsToDate } from "@axtesys/react-tools"

import { Salutation } from "../../api/graphql/types"
import { sanitizeName } from "../../lib/User"
import { selector } from "../../lib/recoil/lib"
import { FeaturesEntity, PermissionsEntity } from "../../types"
import { MIN_REMAINING_JWT_TTL } from "./constants"

const authenticationSchema = z
  .object({
    userId: z.string(),
    salutation: z.enum([
      "Mr",
      "Ms",
      "NonBinary",
      "Enterprise",
      "NotSpecified",
      "Family",
      "Club",
    ]),
    firstName: z.string(),
    lastName: z.string(),
    userRole: z.enum(["Operator", "Manager", "Administrator"]),

    accessToken: z.string(), // JWT
    accessExpiresAt: z.string(), // ISODate
    refreshToken: z.string(), // JWT
    refreshExpiresAt: z.string(), // ISODate

    features: z.optional(FeaturesEntity.schema),
    permissions: z.optional(PermissionsEntity.schema),

    modules: z.array(z.string()).optional(),

    companyId: z.ostring(),
    companyName: z.ostring(),
  })
  .nullable()
  .optional()

export const authenticatedUserState = jsonDbAtom({
  key: "authenticatedUser",
  default: undefined as z.infer<typeof authenticationSchema>,
  schema: authenticationSchema,
})

export const userLoggedInState = atom({
  key: "userLoggedIn",
  default: selector({
    key: `userLoggedIn.default`,
    get: ({ get }) => {
      const authenticatedUser = get(authenticatedUserState)

      if (!authenticatedUser) return false

      // In case the remaining time to live of the
      // refresh token is lower than MIN_REMAINING_JWT_TTL,
      // consider it no longer valid.
      return (
        secondsToDate(authenticatedUser.refreshExpiresAt) >=
        MIN_REMAINING_JWT_TTL
      )
    },
  }),
})

export const operatorSelector = selector({
  key: "operator",
  get: ({ get }) => {
    const operator = get(authenticatedUserState)

    return {
      operatorId: operator?.userId ?? "",
      salutation: operator?.salutation ?? ("NotSpecified" as Salutation),
      fullName: sanitizeName(operator ?? undefined),
    }
  },
})
